import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CheckCircleIcon, 
  XMarkIcon,
  BoltIcon,
  PuzzlePieceIcon,
  CogIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const assemblyProjects = [
    {
      projectName: 'Pneumatikzylinder Montage',
      description: 'Montimi i cilindrit pneumatik. Zgjidhni pjesën që mungon:',
      image: '💨',
      parts: [
        { label: 'O-Ring', de: 'Dichtungsring', correct: true },
        { label: 'Handle', de: 'Griff', correct: false },
        { label: 'Glass', de: 'Glas', correct: false }
      ],
      comment: 'Dichtungsring është vula e nevojshme për të parandaluar rrjedhjen e ajrit.'
    },
    {
      projectName: 'Getriebe Wartung',
      description: 'Mirëmbajtja e kutisë së marsheve. Cili mjet duhet për të hapur kapakun?',
      image: '⚙️',
      parts: [
        { label: 'Screwdriver', de: 'Schraubendreher', correct: true },
        { label: 'Glue', de: 'Klebstoff', correct: false },
        { label: 'Brush', de: 'Bürste', correct: false }
      ],
      comment: 'Schraubendreher përdoret për të liruar vidhat e kapakut të motorit.'
    },
    {
      projectName: 'Elektro-Schaltplan',
      description: 'Lidhja e rrymës. Si thuhet "Kabllot janë të lidhura"?',
      image: '⚡',
      parts: [
        { label: 'Connected', de: 'Verbunden', correct: true },
        { label: 'Broken', de: 'Gebrochen', correct: false },
        { label: 'Missing', de: 'Fehlend', correct: false }
      ],
      comment: 'Verbunden do të thotë që qarku elektrik është i mbyllur dhe funksional.'
    },
    {
      projectName: 'Sicherheits-Check',
      description: 'Kontrolli i sigurisë. Çfarë duhet të mbajë inxhinieri në sy?',
      image: '🥽',
      parts: [
        { label: 'Glasses', de: 'Schutzbrille', correct: true },
        { label: 'Hat', de: 'Hut', correct: false },
        { label: 'Mask', de: 'Maske', correct: false }
      ],
      comment: 'Schutzbrille është e detyrueshme për mbrojtjen e syve gjatë punës teknike.'
    },
    {
      projectName: 'Roboter-Kalibrierung',
      description: 'Kalibrimi i robotit. Cila pjesë bën rrotullimin?',
      image: '🤖',
      parts: [
        { label: 'Axis', de: 'Die Achse', correct: true },
        { label: 'Wire', de: 'Der Draht', correct: false },
        { label: 'Table', de: 'Der Tisch', correct: false }
      ],
      comment: 'Die Achse (Aksi) lejon lëvizjen rrotulluese të krahut të robotit.'
    },
    {
      projectName: 'Hydraulikpumpe Service',
      description: 'Rregullimi i pompës hidraulike. Si thuhet "Presioni"?',
      image: '⚙️',
      parts: [
        { label: 'Pressure', de: 'Der Druck', correct: true },
        { label: 'Speed', de: 'Die Geschwindigkeit', correct: false },
        { label: 'Flow', de: 'Der Fluss', correct: false }
      ],
      comment: '"Der Druck" (presioni) është parametri kryesor që duhet matur në sistemet hidraulike.'
    },
    {
      projectName: 'Schaltschrank Verkabelung',
      description: 'Lidhja e releve në dollapin elektrik. Si thuhet "Siguresa"?',
      image: '🔌',
      parts: [
        { label: 'Fuse', de: 'Die Sicherung', correct: true },
        { label: 'Switch', de: 'Der Schalter', correct: false },
        { label: 'Battery', de: 'Die Batterie', correct: false }
      ],
      comment: '"Die Sicherung" (siguresa) mbron qarkun nga mbingarkesa elektrike.'
    },
    {
      projectName: 'CNC-Fräsmaschine Betrieb',
      description: 'Operimi i makinës CNC. Si thuhet "Vegla/Mjeti"?',
      image: '🔧',
      parts: [
        { label: 'Cutter/Tool', de: 'Das Werkzeug', correct: true },
        { label: 'Metal', de: 'Das Metall', correct: false },
        { label: 'Coolant', de: 'Das Kühlmittel', correct: false }
      ],
      comment: '"Das Werkzeug" (vegla) kryen procesin e prerjes ose gdhendjes në makinën CNC.'
    }
];

const MechatronicsVocabularyGame = () => {
  const [currentProject, setCurrentProject] = useState(null);
  const [score, setScore] = useState(0);
  const [rounds, setRounds] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [completed, setCompleted] = useState(false);
  const { user, updateUserStats } = useAuth();

  const finishGame = useCallback(async () => {
    const totalXp = (user?.xp || 0) + score;
    await updateUserStats({ xp: totalXp });
    setCompleted(true);
  }, [user, score, updateUserStats]);

  const setupRound = useCallback(() => {
    if (rounds >= 10) {
      finishGame();
      return;
    }
    const random = assemblyProjects[Math.floor(Math.random() * assemblyProjects.length)];
    // Shuffle parts
    const shuffledParts = [...random.parts].sort(() => 0.5 - Math.random());
    setCurrentProject({ ...random, parts: shuffledParts });
    setFeedback(null);
  }, [rounds, finishGame]);

  useEffect(() => {
    setupRound();
  }, [setupRound]);

  const handleChoice = (part) => {
    if (feedback) return;
    
    if (part.correct) {
      setScore(prev => prev + 15);
      setFeedback('correct');
    } else {
      setFeedback('wrong');
    }

    setTimeout(() => {
      setRounds(prev => prev + 1);
    }, part.correct ? 1500 : 3000);
  };

  if (completed) {
    return (
      <div className="bg-[#f0f0f0] p-12 rounded-sm border border-[#bcbcbc] shadow-2xl text-center font-sans max-w-2xl mx-auto">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 text-green-600 border border-green-200">
          <ShieldCheckIcon className="w-12 h-12" />
        </div>
        <h2 className="text-2xl font-black text-brand-text mb-2 uppercase italic tracking-tighter">Assembly Cycle Complete</h2>
        <p className="text-brand-text/60 mb-10 text-[10px] font-bold uppercase tracking-widest">Efficiency: 100% | Total XP Gained: {score}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-12 py-3 bg-[#0696d7] text-white rounded-sm font-bold hover:bg-[#057ab0] transition-all active:scale-95 shadow-md uppercase text-xs tracking-widest"
        >
          Initialize Next Batch
        </button>
      </div>
    );
  }

  if (!currentProject) return null;

  return (
    <div className="bg-[#f0f0f0] rounded-sm overflow-hidden shadow-2xl border border-[#bcbcbc] flex flex-col font-sans select-none text-brand-text max-w-4xl mx-auto min-h-[500px]">
      
      {/* Header Area */}
      <div className="bg-[#ffffff] border-b border-[#bcbcbc] p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
           <PuzzlePieceIcon className="w-5 h-5 text-[#0696d7]" />
           <div className="flex flex-col">
              <span className="text-[10px] text-brand-text/50 font-bold uppercase tracking-widest">Skill Module: Assembly Master</span>
              <span className="text-xs font-black text-brand-text tracking-tight">PROJECT REF: {currentProject.projectName}</span>
           </div>
        </div>
        <div className="flex items-center gap-4">
           <div className="text-right">
              <div className="text-[8px] text-brand-text/50 font-black uppercase">Batch Progress</div>
              <div className="text-[#0696d7] font-bold text-xs">{rounds + 1} / 10</div>
           </div>
           <div className="h-8 w-px bg-gray-200"></div>
           <div className="bg-brand-primary/10 px-3 py-1 rounded text-[#0696d7] font-bold text-xs flex items-center gap-2">
              <BoltIcon className="w-3.5 h-3.5" />
              {score} XP
           </div>
        </div>
      </div>

      <div className="flex-1 p-10 flex flex-col lg:flex-row gap-10 items-center">
        
        {/* Visualization Panel */}
        <div className="lg:w-1/2 flex flex-col items-center">
           <div className="w-48 h-48 bg-white border border-[#bcbcbc] rounded shadow-inner flex items-center justify-center text-7xl mb-6 relative group overflow-hidden">
              <div className="absolute inset-0 bg-brand-primary/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              {currentProject.image}
              
              {/* Animation overlays for mechanical feel */}
              {feedback === 'correct' && (
                <div className="absolute inset-0 border-4 border-green-500 animate-pulse pointer-events-none"></div>
              )}
           </div>
           <div className="text-center max-w-xs">
              <p className="text-sm font-bold text-brand-text/80 leading-relaxed italic">{currentProject.description}</p>
           </div>
        </div>

        {/* Selection / Action Panel */}
        <div className="lg:w-1/2 w-full space-y-3">
           <div className="flex items-center gap-2 mb-4">
              <span className="text-[9px] text-brand-text/50 font-bold uppercase tracking-[0.2em]">Select Correct Component</span>
              <div className="flex-1 h-px bg-gray-200"></div>
           </div>

           {currentProject.parts.map((part, i) => {
              let btnStyle = "bg-[#ffffff] border-[#bcbcbc] text-brand-text/80 hover:border-[#0696d7] hover:bg-[#f6faff]";
              if (feedback) {
                if (part.correct) btnStyle = "bg-[#e7f6e7] border-green-500 text-green-700 ring-2 ring-green-100";
                else btnStyle = "bg-gray-100 border-brand-secondary/20 text-brand-text/50 opacity-50";
              }

              return (
                <button
                  key={i}
                  onClick={() => handleChoice(part)}
                  disabled={feedback !== null}
                  className={`w-full p-4 flex items-center justify-between border-2 rounded transition-all group ${btnStyle}`}
                >
                   <div className="flex items-center gap-4">
                      <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center font-mono text-[10px] text-brand-text/50 group-hover:text-[#0696d7]">
                         {i + 1}
                      </div>
                      <div className="flex flex-col text-left">
                         <span className="text-xs font-black uppercase tracking-tight">{part.de}</span>
                         <span className="text-[9px] font-medium opacity-60">{part.label}</span>
                      </div>
                   </div>
                   {feedback === 'correct' && part.correct && <CheckCircleIcon className="w-5 h-5 text-green-500" />}
                </button>
              );
           })}

           {/* Feedback Dialog */}
           <div className={`mt-6 p-4 rounded border transition-all duration-300 ${feedback ? 'opacity-100' : 'opacity-0 pointer-events-none translate-y-2'}`}>
              <div className={`flex gap-3 ${feedback === 'correct' ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'} p-4 rounded border`}>
                 {feedback === 'correct' ? (
                   <CheckCircleIcon className="w-5 h-5 flex-shrink-0 text-green-500" />
                 ) : (
                   <XMarkIcon className="w-5 h-5 flex-shrink-0 text-red-500" />
                 )}
                 <div className="flex flex-col">
                    <span className="text-[9px] font-black uppercase tracking-widest mb-1">
                       {feedback === 'correct' ? 'Analiza: Saktë' : 'Analiza: E Pasaktë'}
                    </span>
                    <p className="text-[11px] font-bold italic">
                       {feedback === 'correct' 
                          ? currentProject.comment 
                          : `Gabim! Përgjigja e saktë ishte "${currentProject.parts.find(p => p.correct).de}". ${currentProject.comment}`
                       }
                    </p>
                 </div>
              </div>
           </div>
        </div>
      </div>

      {/* Footer / Status */}
      <div className="bg-[#e2e2e2] px-6 py-2 border-t border-[#bcbcbc] flex justify-between items-center">
         <div className="flex items-center gap-2">
            <CogIcon className="w-3 h-3 text-brand-text/60 animate-spin-slow" />
            <span className="text-[8px] font-mono text-brand-text/60">SYSTEM STATUS: NOMINAL | FREQ: 50HZ</span>
         </div>
         <span className="text-[8px] font-mono text-brand-text/50">© 2026 GERMAN-TECH ANALYTICS</span>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow { animation: spin-slow 10s linear infinite; }
        ::-webkit-scrollbar { height: 4px; width: 4px; }
        ::-webkit-scrollbar-thumb { background: #ccc; border-radius: 10px; }
        ::-webkit-scrollbar-track { background: transparent; }
      `}} />
    </div>
  );
};

export default MechatronicsVocabularyGame;
